import { useCallback, useEffect, useRef, useState } from "react"
import { cccdAttendance } from "@/lib/api/cccdAttendanceClient"
import { getErrorStatus } from "@/lib/errors"
import {
  enqueueCheckin,
  isNetworkError,
  readQueue,
  removeFromQueue,
  type QueuedCheckin,
} from "@/lib/checkinQueue"
import { toast, toastError } from "@/lib/toast"

const RETRY_MS = 15_000

/**
 * Estado de conexión + cola de check-ins pendientes. Reintenta al volver `online`,
 * cada 15 s mientras haya pendientes, y a demanda con `flush()`.
 */
export function useCheckinQueue() {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine))
  const [pending, setPending] = useState<QueuedCheckin[]>(() => readQueue())
  const [flushing, setFlushing] = useState(false)
  const flushingRef = useRef(false)

  const refresh = useCallback(() => setPending(readQueue()), [])

  const flush = useCallback(async () => {
    if (flushingRef.current) return
    const queue = readQueue()
    if (queue.length === 0) return
    flushingRef.current = true
    setFlushing(true)
    let sent = 0
    const rejected: string[] = []
    try {
      for (const item of queue) {
        try {
          await cccdAttendance.checkin({
            qrToken: item.qrToken,
            attendeeId: item.attendeeId,
            eventDate: item.eventDate,
            method: item.method,
            wristbandGiven: item.wristbandGiven,
          })
          removeFromQueue(item.key)
          sent++
        } catch (err) {
          const status = getErrorStatus(err)
          if (status === 409) {
            // Ya tenía entrada ese día: el envío anterior sí llegó.
            removeFromQueue(item.key)
            sent++
          } else if (isNetworkError(err) || (status !== undefined && status >= 500)) {
            break // Sin red o servidor caído: se conserva el resto y se reintenta luego.
          } else {
            // 404 / 410 / 400: reintentar no ayuda. Se descarta y se avisa al staff.
            removeFromQueue(item.key)
            rejected.push(`${item.label}${status === 410 ? " (boleto revocado)" : status === 404 ? " (boleto no válido)" : ""}`)
          }
        }
      }
    } finally {
      flushingRef.current = false
      setFlushing(false)
      refresh()
    }
    if (sent > 0) toast.success(sent === 1 ? "1 check-in sincronizado." : `${sent} check-ins sincronizados.`)
    if (rejected.length > 0) {
      toastError(`No se pudo registrar la entrada de: ${rejected.join(", ")}. Revísalo manualmente.`)
    }
  }, [refresh])

  const enqueue = useCallback(
    (item: Omit<QueuedCheckin, "queuedAt">) => {
      const added = enqueueCheckin(item)
      refresh()
      return added
    },
    [refresh],
  )

  useEffect(() => {
    const goOnline = () => {
      setOnline(true)
      void flush()
    }
    const goOffline = () => setOnline(false)
    window.addEventListener("online", goOnline)
    window.addEventListener("offline", goOffline)
    void flush()
    return () => {
      window.removeEventListener("online", goOnline)
      window.removeEventListener("offline", goOffline)
    }
  }, [flush])

  useEffect(() => {
    if (pending.length === 0) return
    const id = setInterval(() => void flush(), RETRY_MS)
    return () => clearInterval(id)
  }, [pending.length, flush])

  return { online, pending, flushing, flush, enqueue }
}
